import { estConditionMeteo, libelleMeteo, type ConditionMeteo } from './tarifs'

/**
 * Météo automatique (migration 0061) — côté console dispatch : la partie PURE
 * (types, lecture des lignes, résumé, validation), testée par `meteo.test.ts`.
 * Les accès à la base sont dans `meteoAcces.ts`.
 *
 * La console ne relève JAMAIS la météo elle-même (l'ancien appel Open-Meteo du
 * navigateur est supprimé : usage non commercial seulement). Elle lit ce que
 * l'automate serveur a relevé et décidé (`meteo_etat`, `meteo_episodes`), et
 * transmet les décisions du dispatch par RPC : Arrêter, Forcer. Les réglages
 * (mode, points, seuils) s'écrivent dans `meteo_reglages` (droit `gerer_config`).
 *
 * `platform_config.meteo_actuelle` reste LA condition appliquée aux prix — elle
 * est chargée avec la configuration (`Configuration.meteoActuelle`).
 */

export type ModeMeteo = 'auto' | 'manuel'
export type OrigineMeteo = 'automate' | 'staff' | 'systeme'

export interface PointMeteo {
  code: string
  nom: string
  lat: number
  lon: number
}

export interface ReglagesMeteo {
  mode: ModeMeteo
  points: PointMeteo[]
  pluieSeuilMm: number
  pluiePointsMin: number
  pluieFinMinutes: number
  chaleurActivationC: number
  chaleurDesactivationC: number
  pointChaleur: string
  echecsMax: number
  pushLivreurs: boolean
}

export interface ReleveMeteo {
  code: string
  nom: string
  ok: boolean
  pluie: boolean
  precipitationMm: number | null
  symbole: string | null
  temperatureC: number | null
  erreur: string | null
}

export interface EtatMeteo {
  pluieActive: boolean
  pluieDepuis: string | null
  bloquePluie: boolean
  chaleurActive: boolean
  chaleurDepuis: string | null
  bloqueChaleur: boolean
  forceCondition: ConditionMeteo | null
  forceJusqua: string | null
  forceDepuis: string | null
  echecsConsecutifs: number
  sourceIndisponible: boolean
  derniereErreur: string | null
  dernierReleve: string | null
  dernierSucces: string | null
  prochaineVerification: string | null
  releves: ReleveMeteo[]
  source: string
}

export interface EpisodeMeteo {
  id: string
  condition: ConditionMeteo
  debut: string
  fin: string | null
  origineDebut: OrigineMeteo
  debutPar: string | null
  motifDebut: string | null
  forceJusqua: string | null
  origineFin: OrigineMeteo | null
  finPar: string | null
  motifFin: string | null
}

export interface DonneesMeteo {
  reglages: ReglagesMeteo
  etat: EtatMeteo
  episodes: EpisodeMeteo[]
  /** Nom des membres du staff cités dans le journal (id → nom). */
  noms: Record<string, string>
}

/** Attribution exigée par la licence CC BY 4.0 de MET Norway. */
export const ATTRIBUTION_METEO = 'Données météo : MET Norway'
export const LIEN_ATTRIBUTION_METEO = 'https://api.met.no/'

export const MESSAGE_MIGRATION_0061 =
  "La météo automatique arrive avec la prochaine mise à jour de la base (migration 0061). En attendant, la météo se bascule à la main."

// ── Lecture des lignes ─────────────────────────────────────────────────

const nombre = (v: unknown, d: number): number => {
  const n = typeof v === 'string' ? Number(v) : v
  return typeof n === 'number' && Number.isFinite(n) ? n : d
}
const nombreOuNul = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}
const texte = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null)
const origine = (v: unknown): OrigineMeteo | null =>
  v === 'automate' || v === 'staff' || v === 'systeme' ? v : null

export type Ligne = Record<string, unknown>

export function lireReglagesMeteo(l: Ligne): ReglagesMeteo {
  const points = Array.isArray(l.points)
    ? (l.points as Ligne[])
        .filter((p) => typeof p.code === 'string')
        .map((p) => ({
          code: String(p.code),
          nom: typeof p.nom === 'string' ? p.nom : String(p.code),
          lat: nombre(p.lat, 0),
          lon: nombre(p.lon, 0),
        }))
    : []
  return {
    mode: l.mode === 'manuel' ? 'manuel' : 'auto',
    points,
    pluieSeuilMm: nombre(l.pluie_seuil_mm, 0.3),
    pluiePointsMin: nombre(l.pluie_points_min, 2),
    pluieFinMinutes: nombre(l.pluie_fin_minutes, 30),
    chaleurActivationC: nombre(l.chaleur_activation_c, 40),
    chaleurDesactivationC: nombre(l.chaleur_desactivation_c, 38),
    pointChaleur: texte(l.point_chaleur) ?? 'centre',
    echecsMax: nombre(l.echecs_max, 3),
    pushLivreurs: l.push_livreurs === true,
  }
}

export function lireEtatMeteo(l: Ligne): EtatMeteo {
  const releves = Array.isArray(l.releves)
    ? (l.releves as Ligne[]).map((r) => ({
        code: String(r.code ?? ''),
        nom: typeof r.nom === 'string' ? r.nom : String(r.code ?? ''),
        ok: r.ok === true,
        pluie: r.pluie === true,
        precipitationMm: nombreOuNul(r.precipitationMm),
        symbole: texte(r.symbole),
        temperatureC: nombreOuNul(r.temperatureC),
        erreur: texte(r.erreur),
      }))
    : []
  return {
    pluieActive: l.pluie_active === true,
    pluieDepuis: texte(l.pluie_depuis),
    bloquePluie: l.bloque_pluie === true,
    chaleurActive: l.chaleur_active === true,
    chaleurDepuis: texte(l.chaleur_depuis),
    bloqueChaleur: l.bloque_chaleur === true,
    forceCondition: estConditionMeteo(l.force_condition) ? l.force_condition : null,
    forceJusqua: texte(l.force_jusqu_a),
    forceDepuis: texte(l.force_depuis),
    echecsConsecutifs: nombre(l.echecs_consecutifs, 0),
    sourceIndisponible: l.source_indisponible === true,
    derniereErreur: texte(l.derniere_erreur),
    dernierReleve: texte(l.dernier_releve_at),
    dernierSucces: texte(l.dernier_succes_at),
    prochaineVerification: texte(l.prochaine_verification),
    releves,
    source: texte(l.source) ?? 'met_norway',
  }
}

export function lireEpisode(l: Ligne): EpisodeMeteo {
  return {
    id: String(l.id),
    condition: estConditionMeteo(l.condition) ? l.condition : 'normal',
    debut: String(l.debut),
    fin: texte(l.fin),
    origineDebut: origine(l.origine_debut) ?? 'systeme',
    debutPar: texte(l.debut_par),
    motifDebut: texte(l.motif_debut),
    forceJusqua: texte(l.force_jusqu_a),
    origineFin: origine(l.origine_fin),
    finPar: texte(l.fin_par),
    motifFin: texte(l.motif_fin),
  }
}

// ── Ce que l'écran affiche ─────────────────────────────────────────────

/** Symbole MET Norway (`rainshowers_day`) → libellé français + icône de la console. */
export function libelleSymbole(symbole: string | null): { libelle: string; icone: 'sun' | 'cloud' | 'rain' | 'storm' } {
  if (!symbole) return { libelle: '—', icone: 'cloud' }
  const base = symbole.split('_')[0] ?? ''
  if (base.includes('thunder')) return { libelle: 'Orage', icone: 'storm' }
  if (base.includes('showers')) {
    return { libelle: base.startsWith('heavy') ? 'Fortes averses' : base.startsWith('light') ? 'Averses faibles' : 'Averses', icone: 'rain' }
  }
  if (base.includes('rain') || base.includes('sleet')) {
    return { libelle: base.startsWith('heavy') ? 'Forte pluie' : base.startsWith('light') ? 'Pluie faible' : 'Pluie', icone: 'rain' }
  }
  const table: Record<string, [string, 'sun' | 'cloud']> = {
    clearsky: ['Ciel dégagé', 'sun'],
    fair: ['Plutôt dégagé', 'sun'],
    partlycloudy: ['Partiellement nuageux', 'cloud'],
    cloudy: ['Couvert', 'cloud'],
    fog: ['Brouillard', 'cloud'],
  }
  const t = table[base]
  return t ? { libelle: t[0], icone: t[1] } : { libelle: symbole, icone: 'cloud' }
}

export const libelleOrigine = (o: OrigineMeteo | null): string =>
  o === 'automate' ? 'Automate' : o === 'staff' ? 'Dispatch' : o === 'systeme' ? 'Système' : '—'

/** Heure de BAMAKO (UTC+0, sans heure d'été) — pas celle du navigateur. */
export function heureBamako(iso: string | null): string {
  if (!iso) return '--:--'
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Bamako' })
}

/** Durée lisible entre deux instants : « 25 min », « 2 h 05 », « 3 j ». */
export function duree(debutIso: string, finIso: string | null, maintenant: Date = new Date()): string {
  const min = Math.max(0, Math.round(((finIso ? Date.parse(finIso) : maintenant.getTime()) - Date.parse(debutIso)) / 60_000))
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} h ${String(min % 60).padStart(2, '0')}`
  return `${Math.floor(h / 24)} j`
}

export type SourceDecision = 'manuel' | 'forcage' | 'automate' | 'panne' | 'arret'

export interface Resume {
  /** Condition appliquée aux prix (platform_config). */
  condition: ConditionMeteo
  libelle: string
  /** D'où vient la condition en vigueur. */
  source: SourceDecision
  texteSource: string
  /** Depuis quand (ISO) ; null si inconnu. */
  depuis: string | null
  /** Le dispatch peut-il arrêter quelque chose ? */
  arretable: boolean
  avertissement: string | null
}

/**
 * Résumé de l'état pour la barre et la carte : condition en vigueur, qui l'a
 * décidée, depuis quand. Pur — testé par `meteo.test.ts`.
 */
export function resumer(
  meteoActuelle: ConditionMeteo,
  d: DonneesMeteo,
  maintenant: Date = new Date(),
): Resume {
  const { etat, reglages } = d
  const ouvert = d.episodes.find((e) => e.fin === null && e.condition === meteoActuelle) ?? null
  const forceActif =
    etat.forceCondition !== null && (etat.forceJusqua === null || Date.parse(etat.forceJusqua) > maintenant.getTime())
  const libelle = libelleMeteo(meteoActuelle)
  let source: SourceDecision
  let texteSource: string
  if (reglages.mode === 'manuel') {
    source = 'manuel'
    texteSource = 'Mode manuel : le dispatch bascule la météo'
  } else if (forceActif && etat.forceCondition === meteoActuelle) {
    source = 'forcage'
    texteSource = etat.forceJusqua
      ? `Forcé par le dispatch jusqu'à ${heureBamako(etat.forceJusqua)}`
      : "Forcé par le dispatch jusqu'à arrêt"
  } else if (etat.sourceIndisponible) {
    source = 'panne'
    texteSource = 'Source météo indisponible : retour à Normale'
  } else if (meteoActuelle === 'normal' && (etat.bloquePluie || etat.bloqueChaleur)) {
    source = 'arret'
    texteSource = etat.bloquePluie
      ? `Arrêté par le dispatch : la pluie ne reviendra qu'après ${reglages.pluieFinMinutes} min sans pluie`
      : 'Arrêté par le dispatch : la chaleur ne reviendra qu’après être retombée'
  } else {
    source = 'automate'
    texteSource = 'Automatique (MET Norway)'
  }
  const avertissement = etat.sourceIndisponible
    ? `Source météo indisponible depuis ${etat.echecsConsecutifs} relevés${etat.derniereErreur ? ` (${etat.derniereErreur})` : ''}.`
    : etat.echecsConsecutifs > 0
      ? `Relevé incomplet (${etat.echecsConsecutifs}/${reglages.echecsMax} avant retour à Normale).`
      : null
  return {
    condition: meteoActuelle,
    libelle,
    source,
    texteSource,
    depuis: ouvert?.debut ?? (meteoActuelle === 'rain' ? etat.pluieDepuis : meteoActuelle === 'extreme_heat' ? etat.chaleurDepuis : null),
    arretable: reglages.mode === 'auto' && (meteoActuelle !== 'normal' || forceActif),
    avertissement,
  }
}

/**
 * Bandeau temps réel : que vient de faire l'automate entre deux lectures ?
 * Seules SES décisions s'annoncent (celles du dispatch, il les a prises lui-même).
 */
export function annonceAutomate(avant: DonneesMeteo | null, apres: DonneesMeteo): string | null {
  if (!avant) return null
  if (apres.etat.sourceIndisponible && !avant.etat.sourceIndisponible) {
    return 'Source météo indisponible : la météo repasse en Normale. Vérifiez le ciel et forcez si besoin.'
  }
  const connus = new Map(avant.episodes.map((e) => [e.id, e]))
  for (const e of apres.episodes) {
    const ancien = connus.get(e.id)
    if (!ancien && e.origineDebut === 'automate') {
      return `${libelleMeteo(e.condition)} activée automatiquement à ${heureBamako(e.debut)}${e.motifDebut ? ` — ${e.motifDebut}` : ''}`
    }
    if (ancien && ancien.fin === null && e.fin !== null && e.origineFin === 'automate') {
      return `${libelleMeteo(e.condition)} désactivée automatiquement à ${heureBamako(e.fin)}${e.motifFin ? ` — ${e.motifFin}` : ''}`
    }
  }
  return null
}

/** Relevé du point qui décide de la chaleur (le centre par défaut). */
export function releveCentre(d: DonneesMeteo): ReleveMeteo | null {
  return d.etat.releves.find((r) => r.code === d.reglages.pointChaleur && r.ok) ?? d.etat.releves.find((r) => r.ok) ?? null
}

/** Durées proposées au forçage (minutes) ; null = jusqu'à arrêt. */
export const DUREES_FORCAGE: readonly { minutes: number | null; libelle: string }[] = [
  { minutes: 30, libelle: '30 min' },
  { minutes: 60, libelle: '1 h' },
  { minutes: 120, libelle: '2 h' },
  { minutes: 240, libelle: '4 h' },
  { minutes: null, libelle: "Jusqu'à arrêt" },
]

// ── Réglages saisis ────────────────────────────────────────────────────

export interface ReglagesASaisir {
  pluieSeuilMm: string
  pluiePointsMin: string
  pluieFinMinutes: string
  chaleurActivationC: string
  chaleurDesactivationC: string
  echecsMax: string
  pointChaleur: string
  pushLivreurs: boolean
  points: { code: string; nom: string; lat: string; lon: string }[]
}

export const versSaisie = (r: ReglagesMeteo): ReglagesASaisir => ({
  pluieSeuilMm: String(r.pluieSeuilMm),
  pluiePointsMin: String(r.pluiePointsMin),
  pluieFinMinutes: String(r.pluieFinMinutes),
  chaleurActivationC: String(r.chaleurActivationC),
  chaleurDesactivationC: String(r.chaleurDesactivationC),
  echecsMax: String(r.echecsMax),
  pointChaleur: r.pointChaleur,
  pushLivreurs: r.pushLivreurs,
  points: r.points.map((p) => ({ code: p.code, nom: p.nom, lat: String(p.lat), lon: String(p.lon) })),
})

const decimal = (s: string) => Number(s.trim().replace(',', '.'))
const decimales = (s: string) => (s.trim().replace(',', '.').split('.')[1] ?? '').length

/**
 * Valide ce que l'admin a saisi — mêmes règles que le déclencheur SQL
 * `meteo_reglages_valider`, pour dire l'erreur AVANT l'aller-retour.
 */
export function validerReglagesMeteo(s: ReglagesASaisir): { erreurs: string[]; ligne: Record<string, unknown> | null } {
  const erreurs: string[] = []
  const seuil = decimal(s.pluieSeuilMm)
  const pointsMin = decimal(s.pluiePointsMin)
  const fin = decimal(s.pluieFinMinutes)
  const chaud = decimal(s.chaleurActivationC)
  const tiede = decimal(s.chaleurDesactivationC)
  const echecs = decimal(s.echecsMax)
  if (!(seuil > 0 && seuil <= 20)) erreurs.push('Seuil de pluie : entre 0,01 et 20 mm.')
  if (!Number.isInteger(pointsMin) || pointsMin < 1 || pointsMin > s.points.length)
    erreurs.push(`Points sous la pluie : un entier entre 1 et ${s.points.length}.`)
  if (!Number.isInteger(fin) || fin < 10 || fin > 240) erreurs.push('Fin de pluie : entre 10 et 240 min.')
  if (!(chaud >= 30 && chaud <= 55)) erreurs.push('Forte chaleur : seuil entre 30 et 55 °C.')
  if (!(tiede < chaud)) erreurs.push('La chaleur doit retomber SOUS le seuil d’activation (ex. 38 pour 40).')
  if (!Number.isInteger(echecs) || echecs < 1 || echecs > 10) erreurs.push('Échecs avant retour à Normale : entre 1 et 10.')
  const codes = new Set<string>()
  const points: PointMeteo[] = []
  s.points.forEach((p, i) => {
    const lat = decimal(p.lat)
    const lon = decimal(p.lon)
    const n = `Point ${i + 1}`
    if (!/^[a-z0-9_]{1,30}$/.test(p.code)) erreurs.push(`${n} : code en minuscules, chiffres et _.`)
    if (codes.has(p.code)) erreurs.push(`${n} : code « ${p.code} » en double.`)
    codes.add(p.code)
    if (!p.nom.trim()) erreurs.push(`${n} : nom manquant.`)
    if (!(lat >= -90 && lat <= 90) || !(lon >= -180 && lon <= 180)) erreurs.push(`${n} : coordonnées invalides.`)
    else if (decimales(p.lat) > 4 || decimales(p.lon) > 4) erreurs.push(`${n} : 4 décimales au plus (exigence MET Norway).`)
    points.push({ code: p.code, nom: p.nom.trim(), lat, lon })
  })
  if (s.points.length < 1 || s.points.length > 10) erreurs.push('Entre 1 et 10 points.')
  if (!codes.has(s.pointChaleur)) erreurs.push('Le point de chaleur doit être un des points relevés.')
  if (erreurs.length) return { erreurs, ligne: null }
  return {
    erreurs,
    ligne: {
      pluie_seuil_mm: seuil,
      pluie_points_min: pointsMin,
      pluie_fin_minutes: fin,
      chaleur_activation_c: chaud,
      chaleur_desactivation_c: tiede,
      echecs_max: echecs,
      point_chaleur: s.pointChaleur,
      push_livreurs: s.pushLivreurs,
      points,
    },
  }
}

/** Codes d'erreur de la base → phrases. */
export function messageMeteo(m: string): string {
  if (/meteo_droit_requis|permission denied|42501/.test(m)) return "Vous n'avez pas le droit de piloter la météo."
  if (/meteo_mode_manuel/.test(m)) return 'La météo est en mode manuel : utilisez la bascule Normale / Pluie / Forte chaleur.'
  if (/meteo_duree_invalide/.test(m)) return 'Durée de forçage : entre 5 minutes et 3 jours.'
  if (/meteo_condition_invalide/.test(m)) return 'On ne force que la pluie ou la forte chaleur.'
  if (/meteo_hysteresis_chaleur/.test(m)) return 'La chaleur doit retomber SOUS le seuil d’activation.'
  const r = /meteo_reglages_invalides: (.*)/.exec(m)
  if (r) return `Réglages refusés : ${r[1]}`
  return m || 'Opération refusée.'
}

